import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
/**
 * Practitioners, on the two member surfaces that exist.
 *
 * `PractitionerProfile` is what any signed-in member may read about a
 * practitioner — `GET /practitioners/profiles/{userId}`, the card behind a Mat
 * Bot hit or a VERIFIED author chip. It is deliberately not the admin record:
 * no email, no licence *number* (the body — the College the seal attests —
 * is public; the number identifies a person in a register), no account status.
 *
 * `MyPractitionerProfile` is a practitioner's own row — `GET /me/practitioner`,
 * resolved from the token, licence fields included because they are theirs to
 * correct. Editing a licence field resets the seal to PENDING server-side:
 * `verifiedAt` records when the licence was *checked*, and a number changed
 * afterwards must not keep a badge a parent reads as checked.
 */
export const SealState = z.enum(['PENDING', 'VERIFIED']);
export const PractitionerProfile = z.object({
    userId: z.string().uuid(),
    fullName: z.string(),
    /** The line under the name — *Psychoéducatrice*. Not the speciality. */
    profession: z.string(),
    setting: z.string().nullable(),
    /** Destination ids — the platform's own vocabulary, labels via `useDestinations`. */
    specialityIds: z.array(z.string().uuid()),
    licenceBody: z.string().nullable(),
    seal: SealState,
    verifiedAt: z.string().datetime().nullable(),
    experienceYears: z.number().int().nullable(),
    postCount: z.number().int().nonnegative(),
});
export function usePractitionerProfile(http, userId) {
    return useQuery({
        queryKey: ['practitioners', 'profile', userId],
        queryFn: () => http.get(`/practitioners/profiles/${userId}`, { schema: PractitionerProfile }),
        // A 404 is a state — the author is not a practitioner — not a flake to
        // retry. One attempt, cached, so a thread costs one lookup per author.
        retry: false,
        staleTime: 5 * 60_000,
    });
}
export const MyPractitionerProfile = PractitionerProfile.omit({ postCount: true }).extend({
    email: z.string(),
    licenceNumber: z.string().nullable(),
    accountStatus: z.string(),
    postCount: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
});
export const UpdateMyPractitionerProfile = z
    .object({
    profession: z.string().min(1).max(120),
    setting: z.string().max(160).nullable(),
    licenceBody: z.string().max(120).nullable(),
    licenceNumber: z.string().max(60).nullable(),
    experienceYears: z.number().int().min(0).max(60).nullable(),
    specialityIds: z.array(z.string().uuid()).max(6),
})
    .partial();
/**
 * `enabled` gates on the caller being a practitioner: for anybody else the
 * route is an honest 404, and firing it from a parent's profile screen would
 * put a red request in every session's console for a state that is not an
 * error.
 */
export function useMyPractitionerProfile(http, enabled) {
    return useQuery({
        queryKey: ['me', 'practitioner'],
        queryFn: () => http.get('/me/practitioner', { schema: MyPractitionerProfile }),
        enabled,
    });
}
export function useUpdateMyPractitionerProfile(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (patch) => http.post('/me/practitioner/update', UpdateMyPractitionerProfile.parse(patch), {
            schema: MyPractitionerProfile,
        }),
        onSuccess: (data) => {
            // The write answers the whole row, so the cache is replaced rather than
            // refetched — and the public projection is invalidated because a saved
            // speciality changes what everybody else's card says.
            queryClient.setQueryData(['me', 'practitioner'], data);
            void queryClient.invalidateQueries({ queryKey: ['practitioners', 'profile', data.userId] });
        },
    });
}
//# sourceMappingURL=practitioners.js.map